/**
 * Step 4 — Preview + submit.
 *
 * Pure presentation: receives the wizard state plus an ``onSubmit`` callback
 * from the page. The page owns the submit flow (call createAdminInvoice,
 * navigate on 201, map error.status → wizardState.submitError). Step4Preview
 * just renders.
 */
import React from "react";
import { MONTH_NAMES_DE } from "./MONTH_NAMES";

const sectionTitle =
  "mb-2 text-[11px] font-bold uppercase tracking-[1px] text-[#64748b] dark:text-[#6b7a9a]";
const valueText = "text-sm text-[#0f172a] dark:text-[#eef2ff]";

const ITEM_TYPE_LABEL = {
  rent: "Miete",
  utilities: "Nebenkosten",
  deposit: "Kaution",
  cleaning: "Reinigung",
  custom: "Sonstiges",
};

function fmtChf(n) {
  const v = Number.isFinite(n) ? n : 0;
  return `CHF ${v.toLocaleString("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function tenantDisplayName(t) {
  if (!t) return "—";
  const fn = (t.first_name || "").trim();
  const ln = (t.last_name || "").trim();
  return `${fn} ${ln}`.trim() || (t.name || "").trim() || "—";
}

function tenancyLine(t) {
  if (!t) return "—";
  const unit = [t.unit_short_unit_id, t.unit_title].filter(Boolean).join(" — ");
  return [unit, t.room_name].filter(Boolean).join(" · ") || "—";
}

export default function Step4Preview({ state }) {
  const total = state.items.reduce((sum, it) => {
    const q = parseFloat(it.quantity);
    const p = parseFloat(it.unit_price_chf);
    if (!Number.isFinite(q) || !Number.isFinite(p)) return sum;
    return sum + q * p;
  }, 0);

  return (
    <div className="space-y-6">
      <section>
        <div className={sectionTitle}>Mieter</div>
        <div className={valueText}>{tenantDisplayName(state.selectedTenant)}</div>
        {state.selectedTenant?.email ? (
          <div className="text-[12px] text-[#64748b] dark:text-[#6b7a9a]">
            {state.selectedTenant.email}
          </div>
        ) : null}
      </section>

      <section>
        <div className={sectionTitle}>Mietverhältnis</div>
        <div className={valueText}>{tenancyLine(state.selectedTenancy)}</div>
      </section>

      <section>
        <div className={sectionTitle}>Zeitraum</div>
        <div className={valueText}>
          {MONTH_NAMES_DE[state.billingMonth - 1]} {state.billingYear}
        </div>
      </section>

      <section>
        <div className={sectionTitle}>Zahlungsfrist</div>
        <div className={valueText}>
          {typeof state.paymentTermDays === "number" &&
          Number.isFinite(state.paymentTermDays)
            ? `${state.paymentTermDays} Tage (ab Rechnungsdatum)`
            : "Standard (laut Organisationseinstellungen)"}
        </div>
      </section>

      <section>
        <div className={sectionTitle}>Positionen</div>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-black/10 dark:border-white/[0.07]">
              <th className="py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#6b7a9a]">
                Typ
              </th>
              <th className="py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#6b7a9a]">
                Beschreibung
              </th>
              <th className="py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#6b7a9a]">
                Anzahl
              </th>
              <th className="py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#6b7a9a]">
                Einzelpreis
              </th>
              <th className="py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#6b7a9a]">
                Betrag
              </th>
            </tr>
          </thead>
          <tbody>
            {state.items.map((it, idx) => {
              const q = parseFloat(it.quantity);
              const p = parseFloat(it.unit_price_chf);
              const amt =
                Number.isFinite(q) && Number.isFinite(p) ? q * p : 0;
              return (
                <tr
                  key={idx}
                  className="border-b border-black/5 dark:border-white/[0.04]"
                >
                  <td className="py-2 text-[#0f172a] dark:text-[#eef2ff]">
                    {ITEM_TYPE_LABEL[it.type] || it.type}
                  </td>
                  <td className="py-2 text-[#0f172a] dark:text-[#eef2ff]">
                    {it.description || "—"}
                  </td>
                  <td className="py-2 text-right text-[#0f172a] dark:text-[#eef2ff]">
                    {Number.isFinite(q) ? q : "—"}
                  </td>
                  <td className="py-2 text-right text-[#0f172a] dark:text-[#eef2ff]">
                    {fmtChf(p)}
                  </td>
                  <td className="py-2 text-right font-semibold text-[#0f172a] dark:text-[#eef2ff]">
                    {fmtChf(amt)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4} className="pt-3 text-right text-[11px] font-bold uppercase tracking-[1px] text-[#64748b] dark:text-[#6b7a9a]">
                Total
              </td>
              <td className="pt-3 text-right text-[16px] font-bold text-[#0f172a] dark:text-[#eef2ff]">
                {fmtChf(total)}
              </td>
            </tr>
          </tfoot>
        </table>
      </section>

      {state.submitError ? (
        <div
          role="alert"
          className="rounded-[10px] border border-red-300/40 bg-red-50 p-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/[0.08] dark:text-red-300"
        >
          {state.submitError.message}
        </div>
      ) : null}
    </div>
  );
}
